import React, { useState } from 'react';
import { 
  Box, 
  Flex, 
  VStack, 
  Text, 
  Image, 
  Icon,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { FaThumbtack } from 'react-icons/fa';
import { keyframes } from "@emotion/react";

// Subtle glow animation
const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px 0 rgba(255, 255, 255, 0.3); }
  50% { box-shadow: 0 0 20px 0 rgba(255, 255, 255, 0.4); }
  100% { box-shadow: 0 0 5px 0 rgba(255, 255, 255, 0.3); }
`;

// Define common props for both app types
export interface AppItemProps {
  id: string;
  name: string;
  url: string;
  color?: string;
  isPinned?: boolean;
  isPinMode?: boolean;
  isFullscreen?: boolean;
  description?: string;
  icon?: string;
  thumbnail?: string;
}

// Props for the AppButton component
export interface AppButtonProps {
  item: AppItemProps;
  type: 'theatre' | 'game';
  onPinToggle?: (item: AppItemProps) => void;
  onClick?: () => void;
  isActive?: boolean;
  imageUrl?: string;
  iconName?: string;
}

// Optional callback function for image error handling
export type ImageErrorHandler = (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;

const AppButton: React.FC<AppButtonProps> = ({
  item,
  type,
  onPinToggle,
  onClick,
  isActive = false,
  imageUrl,
  iconName,
}) => {
  const { id, name, url, color = 'blue', isPinned = false, isPinMode = false } = item;
  const pulseAnimation = `${pulseGlow} 3s infinite`;
  const [isLoading, setIsLoading] = useState(iconName !== 'letter');
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };
  
  const handleImageLoad = () => setIsLoading(false);

  // Handle pin toggle action
  const handlePinToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onPinToggle) {
      onPinToggle(item);
    }
  };

  // Handle click action based on mode
  const handleClick = (e: React.MouseEvent) => {
    if (isPinMode) {
      e.preventDefault();
      if (onPinToggle) {
        onPinToggle(item);
      }
    } else if (onClick) {
      e.preventDefault();
      onClick();
    } else {
      // Default behavior - navigate to URL
      if (item.isFullscreen) {
        e.preventDefault();
        const redirectUrl = `https://www.youtube.com/redirect?q=${encodeURIComponent(url)}`;
        window.open(redirectUrl, '_blank');
      } else if (type === 'theatre') {
        e.preventDefault();
        window.location.href = url;
      }
      // Game navigation is handled by the onClick prop
    }
  };

  // Determine the src for the image
  const getSrc = () => {
    if (imageUrl) {
      return imageUrl;
    } else if (iconName === 'letter') {
      // Return null for letter icon - we'll handle this separately
      return null;
    } else if (iconName && type === 'theatre') {
      try {
        // Use a direct path to the assets folder
        return new URL(`../assets/appIcons/${iconName}`, import.meta.url).href;
      } catch (e) {
        console.warn(`Could not load icon: ${iconName}`, e);
        return 'https://via.placeholder.com/150';
      }
    }
    return 'https://via.placeholder.com/150';
  };

  const getFirstLetter = (nameStr: string | undefined): string => {
    return nameStr?.trim()?.charAt(0)?.toUpperCase() || "?";
  };

  // Determine background color for letter icon based on name
  const getLetterBgColor = () => {
    const colors = [
      "blue.500", "purple.500", "green.500", "red.500", "orange.500", 
      "teal.500", "cyan.500", "pink.500", "yellow.500"
    ];
    
    // Create a simple hash from the site name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    
    // Use the hash to pick a color
    return colors[hash % colors.length];
  };

  return (
    <VStack
      spacing={3}
      align="center"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-5px) scale(1.05)' }}
      role="group"
      position="relative"
      className={type === 'theatre' ? 'app-button' : 'game-button'}
      cursor="pointer"
      onClick={handleClick}
      opacity={isActive || isPinned ? 1 : type === 'game' ? 0.7 : 1}
      transform={isActive ? "scale(1.05)" : "scale(1)"}
    >
      <Flex
        w="150px"
        h="150px"
        align="center"
        justify="center"
        borderRadius="24px"
        bg={type === 'theatre' ? "black" : "black"}
        color={`${color}.400`}
        backdropFilter="blur(10px)"
        borderWidth={isActive && type === 'game' ? "2px" : "1px"}
        borderColor={isActive && type === 'game' 
          ? `${color}.500` 
          : "rgba(255, 255, 255, 0.1)"}
        boxShadow={isActive && type === 'game' 
          ? `0 0 20px rgba(66, 153, 225, 0.5)` 
          : "0 8px 20px rgba(0, 0, 0, 0.4)"}
        position="relative"
        overflow="hidden"
        transition="all 0.3s ease"
        animation={isActive && type === 'game' ? pulseAnimation : "none"}
        _hover={{ 
          color: `${color}.300`,
          borderColor: isActive && type === 'game' 
            ? `${color}.500` 
            : "rgba(255, 255, 255, 0.2)",
          animation: type === 'theatre' ? pulseAnimation : undefined
        }}
        _after={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at center, rgba(${color === 'blue' ? '37, 99, 235' : color === 'purple' ? '124, 58, 237' : color === 'red' ? '220, 38, 38' : '5, 150, 105'}, 0.1) 0%, rgba(0, 0, 0, 0) 70%)`,
          opacity: 0.6,
          zIndex: 0
        }}
      >
        <Box position="relative" zIndex={1} width="100%" height="100%">
          {isLoading && getSrc() && (
            <Flex 
              width="100%" 
              height="100%" 
              justify="center" 
              align="center"
            >
              <Spinner color={`${color}.400`} size="xl" />
            </Flex>
          )}

          {/* Fallback if image doesn't load or if iconName is set to 'letter' */}
          {(!getSrc() || imageError || iconName === 'letter') ? (
            <Flex
              width="100%"
              height="100%"
              justify="center"
              align="center"
              bg={getLetterBgColor()}
              borderRadius="24px"
            >
              <Text fontSize="60px" fontWeight="bold" color="white">
                {getFirstLetter(name)}
              </Text>
            </Flex>
          ) : (
            <Image
              src={getSrc() || ""}
              alt={name}
              width="100%"
              height="100%"
              objectFit={type === 'theatre' ? "contain" : "cover"}
              onLoad={handleImageLoad}
              onError={handleImageError}
              display={isLoading ? "none" : "block"}
            />
          )}
        </Box>
      </Flex>

      {isPinMode && (
        <IconButton
          aria-label={isPinned ? "Unpin" : "Pin"}
          icon={<Icon as={FaThumbtack} />}
          position="absolute"
          top={-2}
          right={-2}
          size="sm"
          borderRadius="full"
          bg="rgba(0, 0, 0, 0.8)"
          color={isPinned ? "blue.400" : "gray.400"}
          _hover={{ bg: "rgba(0, 0, 0, 0.9)", color: isPinned ? "blue.300" : "gray.300" }}
          onClick={handlePinToggle}
          zIndex={2}
          transform={isPinned ? "none" : type === 'game' ? "rotate(45deg)" : undefined}
          boxShadow={isPinned ? "0 0 10px rgba(66, 153, 225, 0.6)" : "none"}
          transition="all 0.2s ease"
        />
      )}

      <Text 
        fontSize="sm" 
        fontWeight={isActive ? "bold" : "normal"} 
        color={isActive ? "white" : "gray.300"}
        textAlign="center"
        noOfLines={2}
        width="100%"
        wordBreak="break-word"
        maxWidth="150px"
      >
        {name}
      </Text>
    </VStack>
  );
};

export default AppButton; 